import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import MovieCard from "@/components/feature/MovieCard";
import usePageMeta from "@/hooks/usePageMeta";
import { getHotMovies, hotKeywords, searchMovies } from "@/lib/movieData";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get("q") || "";
  const [input, setInput] = useState(keyword);

  usePageMeta({
    title: keyword ? `${keyword} - 搜索结果 - 秋霞电影` : "搜索电影 - 秋霞电影",
    description: keyword
      ? `在秋霞电影搜索「${keyword}」，查看相关电影资料、上映信息与影视资讯推荐。`
      : "在秋霞电影搜索电影片名、导演、演员或类型，快速找到想看的影片资料。",
    keywords: keyword
      ? `${keyword},电影搜索,秋霞电影,电影资讯`
      : "电影搜索,电影关键词,秋霞电影,电影资讯",
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      const value = input.trim();
      if (value === keyword) return;
      if (value) next.set("q", value);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 350);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input]);

  const results = useMemo(() => {
    const list = searchMovies(keyword);
    return [...list].sort((a, b) => {
      const aTitle = a.title.includes(keyword) ? 1 : 0;
      const bTitle = b.title.includes(keyword) ? 1 : 0;
      if (aTitle !== bTitle) return bTitle - aTitle;
      return b.views - a.views;
    });
  }, [keyword]);

  const hot = getHotMovies(4);

  return (
    <PageLayout>
      <section className="w-full bg-background-50 border-b border-background-200">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-10 md:py-14">
          <p className="text-[11px] tracking-[0.3em] text-foreground-400 uppercase">Search</p>
          <h1 className="mt-3 font-heading text-3xl md:text-4xl text-foreground-950">搜索电影</h1>
          <p className="mt-3 text-sm text-foreground-600">
            支持片名、导演、演员与类型的模糊匹配，输入关键词即可实时刷新结果。
          </p>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-[720px]">
            <div className="relative flex-1">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                type="text"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="输入电影名称、导演或类型，如「悬疑」「深空回声」"
                aria-label="搜索关键词"
                className="w-full h-12 pl-12 pr-4 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              {input && (
                <button
                  type="button"
                  aria-label="清空关键词"
                  onClick={() => setInput("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:bg-background-100 cursor-pointer"
                >
                  <i className="ri-close-line"></i>
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                const next = new URLSearchParams(searchParams);
                if (input.trim()) next.set("q", input.trim());
                else next.delete("q");
                setSearchParams(next, { replace: true });
              }}
              className="h-12 px-7 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </div>

          <div className="mt-5 flex items-center gap-2 flex-wrap">
            <span className="text-xs text-foreground-500">热门关键词：</span>
            {hotKeywords.map((word) => (
              <button
                key={word}
                type="button"
                onClick={() => setInput(word)}
                className={`h-8 px-3 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                  keyword === word
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {word}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="w-full bg-background-100">
        <div className="max-w-[1180px] mx-auto px-4 md:px-6 py-9 md:py-12">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <p className="text-sm text-foreground-700">
              {keyword ? (
                <>
                  关键词 <span className="text-primary-600 font-medium">「{keyword}」</span> 找到{" "}
                  <span className="text-accent-600 font-medium">{results.length}</span> 条相关结果
                </>
              ) : (
                <>输入关键词开始搜索，或从下方热门影片中挑选</>
              )}
            </p>
            {keyword && (
              <button
                type="button"
                onClick={() => setInput("")}
                className="text-xs text-foreground-500 hover:text-accent-600 transition-colors cursor-pointer self-start whitespace-nowrap"
              >
                清空搜索条件
              </button>
            )}
          </div>

          {results.length > 0 && (
            <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-8">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}

          {results.length === 0 && keyword && (
            <div className="mt-8 rounded-lg border border-background-200 bg-background-50 px-6 py-14 text-center">
              <span className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-eye-line text-2xl"></i>
              </span>
              <h2 className="mt-5 font-heading text-xl text-foreground-950">
                没有找到与「{keyword}」相关的内容
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                换个关键词试试，或从下方热门关键词与推荐影片继续浏览。
              </p>
              <div className="mt-5 flex flex-wrap justify-center gap-2">
                {hotKeywords.slice(0, 5).map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => setInput(word)}
                    className="h-8 px-3 rounded-full border border-background-300 text-xs text-foreground-600 hover:border-accent-300 hover:text-accent-700 transition-colors cursor-pointer"
                  >
                    {word}
                  </button>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Link
                  to="/"
                  className="h-10 px-5 inline-flex items-center rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors"
                >
                  返回首页
                </Link>
                <Link
                  to="/category/drama"
                  className="h-10 px-5 inline-flex items-center rounded-md border border-background-300 text-foreground-700 text-sm hover:border-primary-400 transition-colors"
                >
                  浏览全部影片
                </Link>
              </div>
            </div>
          )}

          {!keyword && (
            <div className="mt-8">
              <div className="flex items-end justify-between gap-3 mb-5">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950">编辑推荐</h2>
                <span className="text-xs text-foreground-500">按站内浏览量</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 md:gap-x-5 gap-y-8">
                {hot.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}